import { config } from '@/config';
import api from '@/client/axiosInstance';
import { Chat } from '@/types';
import { UploadFile } from 'antd';
import { RcFile } from 'antd/es/upload';
import { AxiosError } from 'axios';
import { withErrorCode } from '@/helpers/subscriptionError';

export const getAllChats = async (
  page: number,
  limit: number,
): Promise<{
  data: Chat[];
  total: number;
}> => {
  try {
    const response = await api.get(`${config.VITE_BE_ENDPOINT}chats`, {
      params: {
        page,
        limit,
      },
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data?.message ||
          'Something went wrong. Please try again.',
      );
    } else {
      throw new Error('Unknown error occurred');
    }
  }
};

export const getChatById = async (id: string): Promise<Chat> => {
  try {
    const response = await api.get(
      `${config.VITE_BE_ENDPOINT}chats/${id}`,
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data?.message ||
          'Something went wrong. Please try again.',
      );
    } else {
      throw new Error('Unknown error occurred');
    }
  }
};

export const createChat = async (participants: string[]): Promise<Chat> => {
  try {
    const response = await api.post(
      `${config.VITE_BE_ENDPOINT}chats`,
      {
        participants,
      },
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      // TCX-900: preserve the server error code (see api/booking.ts) so an
      // unverifiable subscription is told apart from a real chat failure.
      throw withErrorCode(
        new Error(
          error.response?.data?.message ||
            'Something went wrong. Please try again.',
        ),
        error.response?.data?.error,
      );
    } else {
      throw new Error('Unknown error occurred');
    }
  }
};

export const uploadFile = async (
  file: RcFile,
): Promise<{ imageUrl: string }> => {
  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await api.post('chats/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data?.message ||
          'Something went wrong. Please try again.',
      );
    } else {
      throw new Error('Unknown error occurred');
    }
  }
};

export const getUnreadMessages = async (
  userId: string | undefined,
): Promise<{ unreadCount: number }> => {
  if (!userId) return { unreadCount: 0 };
  try {
    const response = await api.get(
      `${config.VITE_BE_ENDPOINT}chats/messages/unread-count`,
      {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
        },
      },
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data?.message ||
          'Something went wrong. Please try again.',
      );
    } else {
      throw new Error('Unknown error occurred');
    }
  }
};
